import Store from 'store/store';
import { getDeviceInfo } from 'utils/bolt/client';
import { accessNested, commaSeparatedList, parseDate, toQueryString, trimChar } from 'utils/utils';
// import cached from 'utils/cached';
import stringWithParams from 'utils/stringWithParams';

export function buildClientStoreData(state) {
	Object.assign(state.client, getDeviceInfo());
	return state;
}

export function getValueForClientSize(variable = {}, client, options = {}) {
	if (typeof variable === 'string' || !variable) {
		return variable;
	}

	if (!client) {
		client = Store.get().client;
	}

	let {
		deviceSize = 'mobile',
		orientation = 'portrait'
	} = client;

	if (deviceSize === 'largeDesktop' || deviceSize === 'smallDesktop') deviceSize = 'desktop';

	let deviceOrientation = `${deviceSize}${orientation[0].toUpperCase() + orientation.slice(1)}`;

	if (options.nullIfDisabled) {
		// if you want to return nothing when the relevant item in thundr is disabled,
		// instead of using the fallback logic.
		// ex: don't show TT on mobile landscape if "mobileLanscape" is disabled in "variable", but use the next
		// available asset if "mobileLandscape" is an empty string.
		if (variable[deviceOrientation] === undefined && variable[deviceSize] === undefined) {
			return null;
		}
	}

	if (deviceOrientation === 'mobileLandscape' && !variable.mobileLandscape) {
		deviceOrientation = 'tabletLandscape';
	}

	if (deviceOrientation === 'tabletLandscape' && !variable.tabletLandscape) {
		deviceOrientation = 'desktopLandscape';
		deviceSize = 'desktop';
	}

	return variable[deviceOrientation] || variable[deviceSize] || variable.desktop || variable.tablet || variable.mobile;
}

const replaceVH = str => typeof str === 'string' ? str.replace(/\b(\d+)vh\b/g, 'calc($1 * var(--vh))') : str;

export function getSizeOverride(data) {
	const size = getValueForClientSize(data);
	if (!size) {
		return size;
	}
	if (typeof size === 'string') {
		return replaceVH(size);
	}
	return Object.fromEntries(
		Object.entries(size).map(([key, value]) => [key, replaceVH(value)])
	);
}

export function getTTSizeOverride(tt) {
	return getSizeOverride(tt?.titleTreatmentSize) || {};
}

export function normalizeRegion(region) {
	if (!region || typeof region !== 'string') {
		return null;
	}
	region = region.toUpperCase().replace(/^([A-Z]{2})_.+$/, '$1');
	return region.length === 2 ? region : null;
}

export function getPagePath(pageData) {
	let path = pageData?.path || '/';
	let hash = '';
	if (path.includes('#')) {
		[path, hash] = path.split('#');
		path = path || '/';
		if (pageData.section) {
			const parent = pageData.parent || 'boltHome';
			path = Store.get().pages?.[parent]?.path || path;
		}
	}
	if (pageData?.baseHtml) {
		path += pageData.baseHtml;
	}
	return { path, hash };
}

export function getRelativeRoot(pageDataOrPath) {
	if (!pageDataOrPath) return undefined;
	const pagePath = typeof pageDataOrPath === 'string' ? pageDataOrPath : getPagePath(pageDataOrPath).path;
	return pagePath.slice(1).split('/').slice(1).fill('../').join('');
}

export function getRelativePageUrl(page, queryString = typeof window !== 'undefined' ? location.search : '', hashInPage, relativeRoot) {
	let store = Store.get();
	let activePage = store.activePage;
	let newPageData = typeof page === 'string' ? store.appData?.pages?.[page] : page;
	if (!newPageData) {
		return null;
	}
	if (typeof queryString === 'object') {
		queryString = toQueryString(queryString);
	}
	// let qs = !ignoreQueryString && typeof window !== 'undefined' ? location.search : '';

	let { path, hash } = getPagePath(newPageData);
	hash = hash || hashInPage || '';
	if (hash && !hash.startsWith('#')) {
		hash = '#' + hash;
	}

	if (relativeRoot == undefined) {
		let currentPageData = store?.appData?.pages?.[activePage];
		relativeRoot = getRelativeRoot(currentPageData);
	}
	if (relativeRoot == undefined || relativeRoot === false) {
		return path + queryString + hash;
	}

	return (relativeRoot + path.slice(1) + queryString + hash) || './';
}

export function getPageIdFromURL(pages) {
	let url = location.pathname + location.hash;
	if (__IS_LOCAL__) url = url.replace('/preview/', '/');
	let top = Object.values(pages || Store.get().appData.pages || {}).flatMap(page => {
		return [page.path, ...commaSeparatedList(page.alternatePaths)].map(path => {
			if (page.baseHtml) {
				path = path?.startsWith('#') ? page.baseHtml + path : path + page.baseHtml;
			}
			return url.includes(path) ? { id: page.parent || page.id, count: path.length } : null;
		});
	}).filter(e => e).sort((a, b) => a.count - b.count).pop();
	return top?.id;
}

export function getMainPage(appData = Store.get().appData) {
	const pageList = Object.values(appData.pages || {});
	const homePage = pageList.find(page => page.isMainPage) || pageList.find(page => page.page === 'home') || pageList[0];
	return homePage ? homePage.id : null;
}

export function isVideosPage(id) {
	return Store.get().appData.pages[id]?.type === 'videos';
}

/**
 * Get the page ID of the videos page that contains the specified video ID.
 * @param {string} id video ID (YouTube ID, Vimeo ID, or videoSrc)
 * @param {string} playlistId (optional) YouTube playlist ID to help identify the correct videos page
 * @returns {string} the ID of the videos page or 'boltVideos' if not found
 */
export function getPageFromVideoId(id, playlistId) {
	const videoPages = Object.values(Store.get().appData.pages).filter(page => page.type === 'videos');
	const page = videoPages.find(page => Object.values(page.data?.list).find(video =>
		(video.youtubeId === id || video.vimeoId === id || video.videoSrc === id || (playlistId && video.youtubePlaylistId === playlistId))
	));
	return page?.id || 'boltVideos';
}

export function shouldShowShowtimes(forceLive, goLiveDate) {
	if (!arguments.length) {
		const store = Store.get();
		forceLive = accessNested(store, 'appData.options.forceLive');
		goLiveDate = parseDate(accessNested(store, 'appData.meta.dates.live') || accessNested(store, 'appData.meta.dates.release'));
	}
	return forceLive || goLiveDate <= Date.now();
}

export const isDualViewEnabled = pages => {
	const appData = Store.get().appData;

	if (!appData?.options?.dualView) {
		return false;
	}

	if (!pages) {
		pages = Object.values(appData.pages);
	}

	const hasShowtimes = pages.find(page => getPageType(page) === 'showtimes');
	const hasHomeEnt = pages.find(page => getPageType(page) === 'homeEnt');
	const hasWidget = pages.find(page => getPageType(page) === 'stickAndScaleWidget');

	return (hasShowtimes && shouldShowShowtimes() && hasHomeEnt) || (hasShowtimes && hasHomeEnt && hasWidget);
};

export const getPageType = page => (
	page.component || page.type
);

export function addCSSRuleset(selector, declarations) {
	const style = document.createElement('style');
	const sheet = document.head.appendChild(style).sheet;

	if (Array.isArray(declarations)) {
		declarations = declarations.join(';');
	}
	if (declarations && typeof declarations === 'object') {
		declarations = Object.entries(declarations).map(([key, val]) => `${key}:${key === 'content' ? "'" + val + "'" : val}`).join(';');
	}
	sheet.insertRule(selector + '{' + declarations + '}', sheet.cssRules.length);
}

export function splitQueryAndPath(fullPath) {
	if (fullPath.includes('?')) {
		let [path, query] = fullPath.split('?');
		query = '?' + query;
		return { path, query };
	}
	return { path: fullPath };
}

export function navigateInternally(destination, opts = {}) {
	// Supports the following link formats:
	// - path
	// - path#section
	// - pageId
	// - pageId#section
	// - @reservedVariable
	// - @reservedVariable::?queryString
	// - videoPageId?v=videoId

	if (destination.startsWith('@')) {
		return Store.emit('OPEN_RESERVED_VARIABLE_LINK', destination + (opts.queryString || ''));
	}

	if (destination.includes('?v=')) {
		const stringArray = destination.split('?v=');
		destination = stringArray[0];
		opts.queryString = '?v=' + stringArray[1];
	}

	let hashInPage = undefined;
	if (destination.includes('#') && !destination.startsWith('#')) {
		const pageAndHash = destination.match(/^([\w\d_-]+)#([\w\d_-]+)$/);
		if (pageAndHash?.length === 3) {
			destination = pageAndHash[1];
			hashInPage = `#${pageAndHash[2]}`;
		}
	}

	const page = getPageFromIdOrPath(destination);

	if (!page) {
		return;
	}

	if (!page.section && !page.type && destination !== 'boltHome') {
		Store.emit('CLOSE_ALL_OVERLAYS');
		Store.emit('SET_ACTIVE_PAGE', page.id || destination, opts?.queryString, { hashInPage });
		return;
	}

	if (page?.type === 'showtimes') {
		// TODO: compute calendarRemindMode instead of expecting it as an option?
		if (opts?.calendarRemindMode) {
			Store.emit('SET_ACTIVE_PAGE', getMainPage(), opts?.queryString, { hashInPage });
			Store.emit('SHOW_CAL_REMIND');
		} else {
			// if (page.data?.groupBookings?.isEnabled) queryString = { ...parseQueryString(opts?.queryString), groupbookings: true };
			Store.emit('SHOWTIMES_SHOW', opts?.animate, opts?.queryString, page?.id);
		}
	} else if (page?.type === 'homeEnt') {
		Store.emit('HOMEENT_SHOW', opts?.animate, opts?.queryString, page?.id);
	} else if (page?.type === 'pressKit') {
		Store.emit('PRESSKIT_SHOW', undefined, undefined, destination);
	} else if (page?.type === 'videos') {
		Store.emit('OPEN_VIDEO_OVERLAY', page.id || destination, opts?.queryString);
	} else if (page.parent && page.parent === Store.get().activePage) {
		// if the destination page is a child of the currently active page, just scroll to destination section
		Store.emit('CHANGE_ACTIVE_SECTION', page.id);
	} else if (page.parent) {
		// if the destination page is a child of a page that is not the currently active page,
		// navigate to the parent page then scroll to the destination section
		hashInPage = page.path?.match(/#(.+)$/)?.[1] || page.data?.hash || hashInPage;
		Store.emit('CLOSE_ALL_OVERLAYS');
		Store.emit('SET_ACTIVE_PAGE', page.parent, opts?.queryString, { hashInPage });
	} else {
		Store.emit('CLOSE_ALL_OVERLAYS');
		// TODO: REVISIT
		Store.emit('SET_ACTIVE_PAGE', page.id || destination, opts?.queryString, { hashInPage });
		Store.emit('CHANGE_ACTIVE_SECTION', page.id || destination, { instant: opts?.instant !== false });
		if (page?.type === 'stickAndScaleWidget') {
			Store.emit('EXPAND_WIDGET');
		}
	}
}

const isObjectOrArray = v => typeof v === 'object' && v !== null;
function _merge(source, data) {
	if (!isObjectOrArray(data)) {
		return source;
	}
	Object.entries(data).forEach(([key, value]) => {
		if (!isObjectOrArray(value)) {
			source[key] = value;
			return;
		}
		source[key] = _merge(isObjectOrArray(source[key]) ? source[key] : {}, value);
	});
	return source;
}
function merge(source, ...objects) {
	return objects.reduce((merged, obj) => _merge(merged, obj), source);
}

export function getPageMeta({ store = Store.get(), appData = store.appData, page = store.activePage } = {}) {
	let metaTags = appData?.doc?.metaTags;
	let pageSpecificMeta = metaTags?.pageSpecificMetaTags?.[page];
	return merge({}, metaTags, pageSpecificMeta, appData?.pages?.[page]?.meta);
}

export function getPageTitle(data) {
	let store = data?.store || Store.get();
	let appData = data?.appData || store.appData;
	let page = data?.page || store.activePage;
	let meta = data?.meta || getPageMeta({ store, appData, page });
	let title = meta.title;
	let pageData = appData.pages?.[page];
	let pageName = pageData ? stringWithParams(appData.copy?.baseCopy, pageData.name) : '';
	return stringWithParams({ ...appData.copy?.baseCopy, PAGE_NAME: pageName }, title);
}

export function getPageDescription(data) {
	let store = data?.store || Store.get();
	let appData = data?.appData || store.appData;
	let page = data?.page || store.activePage;
	let meta = data?.meta || getPageMeta({ store, appData, page });
	let description = meta.description;
	let pageData = appData.pages?.[page];
	let pageName = pageData ? stringWithParams(appData.copy?.baseCopy, pageData.name) : '';
	return stringWithParams({ ...appData.copy?.baseCopy, PAGE_NAME: pageName }, description);
}

export function getHeaderHeight() {
	const header = document.querySelector('#header');
	return header?.clientHeight || 0;
}

/** AVOID - please use actual links in the HTML instead of this */
export function linkOut(href, target) {
	const a = document.createElement('a');
	a.href = href;
	a.target = target || '_blank';
	a.rel = 'noopener';
	a.click();
}

export function	arrayWithUniqueKeys(array, key = 'key') {
	// let map = array.reduce((map, obj) => map.set(obj?.[key], obj), new Map());
	// return [...map.values()];
	return Object.values(Object.fromEntries(array.map(e => [e?.[key], e])));
}

export function getSectionsWithContent(sections) {
	if (typeof window === 'undefined') {
		return [];
	}
	return [...document.querySelectorAll('[data-section]')].filter(section => {
		return sections.find(s => s.id === section.getAttribute('data-section')) && section.hasChildNodes();
	});
}

export function getShowtimesData() {
	let store = Store.get();
	// TODO: check store.showtimes.show rather than activePage?
	let activePage = store.activePage;
	const pageType = store.appData?.pages?.[activePage]?.type;
	const pageId = (pageType === 'showtimes' || pageType === 'filmPage') ? activePage : 'boltShowtimes';
	let pageData = store.appData?.pages?.[pageId]?.data || {};
	if (pageType === 'filmPage') {
		pageData = Object.values(store.appData?.pages?.[activePage]?.data || {}).find(section => section.type === 'showtimes')?.data || {};
	}
	return pageData;
}

export function onMarkdownClick(e) {
	const elem = e.target.closest('a[data-track][data-action]');
	if (elem) {
		const isSameWindowLink = !elem.target || elem.target === '_self' || elem.target === '_top';
		const opensInSameWindow = isSameWindowLink && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey && e.button === 0;
		if (opensInSameWindow) {
			e.preventDefault();
		}
		const action = elem.getAttribute('data-action');
		const category = elem.getAttribute('data-track');
		const label = elem.getAttribute('data-label');
		Store.emit('CUSTOM_EVENT', { action, category, label });
		if (opensInSameWindow) {
			location.href = elem.href;
		}
	}

	if (e.target.matches('a[href^="@"]')) {
		e.preventDefault();
		return Store.emit('OPEN_RESERVED_VARIABLE_LINK', e.getAttribute('href'));
	}
	let internalLink = e.target.closest('a[href^="#$"]');
	if (internalLink) {
		e.preventDefault();
		let page = internalLink.getAttribute('href').slice(2).trim();
		let navOptions = {};
		if (page.includes('/?')) {
			let [pageId, qs] = page.split('/?');
			page = pageId;
			navOptions.queryString = '?' + qs;
		}
		navigateInternally(page, navOptions);
	}
}

/**
 * Get page data from page ID or path.
 * @param {string} destination - page ID or path
 * @returns {object|null} page data or null if not found
 */
export function getPageFromIdOrPath(destination) {
	destination = destination?.split('?')?.[0]; // Remove query string if present
	const pages = Store.get().appData?.pages;

	if (!destination) return null;
	if (!pages) return null;

	if (pages[destination]) return pages[destination];

	const formattedDestination = trimChar(destination, '/');
	return Object.values(pages).find(
		e => formattedDestination === trimChar(e.path, '/') || formattedDestination === trimChar(e.additionalPaths, '/')
	) || null;
}

export function getScroller() {
	let mainScroll = document.getElementById('mainScroll');
	if (!mainScroll || getComputedStyle(mainScroll).overflowY === 'visible') {
		return document.scrollingElement;
	}
	return mainScroll;
}